import { canvasToMask, maskToCanvas } from '../../lib/mask';

export type Tool = 'paint' | 'erase';
type Op = { kind: 'stroke'; tool: Tool; width: number; points: [number, number][] } | { kind: 'reset' };

/**
 * Paint/erase on a mask at working-image size. History is a list of strokes
 * replayed over the starting mask, so undo depth is unlimited and cheap.
 */
export class MaskPainter {
  readonly display: HTMLCanvasElement;
  private mask: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private history: Op[] = [];
  private current: Extract<Op, { kind: 'stroke' }> | null = null;
  private frame = 0;

  constructor(
    private start: Uint8Array,
    private detected: Uint8Array,
    readonly width: number,
    readonly height: number,
    private tint: string,
    private onChange: () => void,
  ) {
    this.mask = maskToCanvas(start, width, height);
    this.ctx = this.mask.getContext('2d', { willReadFrequently: true })!;
    this.display = document.createElement('canvas');
    this.display.width = width;
    this.display.height = height;
    this.redraw();
  }

  get canUndo() {
    return this.history.length > 0;
  }

  begin(tool: Tool, width: number, x: number, y: number) {
    this.current = { kind: 'stroke', tool, width, points: [[x, y]] };
    this.drawSegment(this.current, 0);
    this.scheduleRedraw();
  }

  move(x: number, y: number) {
    const s = this.current;
    if (!s) return;
    s.points.push([x, y]);
    this.drawSegment(s, s.points.length - 1);
    this.scheduleRedraw();
  }

  end() {
    if (this.current) this.history.push(this.current);
    this.current = null;
    this.onChange();
  }

  undo() {
    this.history.pop();
    this.replay();
  }

  resetToDetected() {
    this.history.push({ kind: 'reset' });
    this.replay();
  }

  result(): Uint8Array {
    // canvasToMask reads red; the mask canvas stores coverage in alpha, so flatten first.
    const c = document.createElement('canvas');
    c.width = this.width;
    c.height = this.height;
    const ctx = c.getContext('2d', { willReadFrequently: true })!;
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.drawImage(this.mask, 0, 0);
    return canvasToMask(c);
  }

  private replay() {
    let base = this.start;
    let from = 0;
    this.history.forEach((op, i) => {
      if (op.kind === 'reset') {
        base = this.detected;
        from = i + 1;
      }
    });
    maskToCanvas(base, this.width, this.height, this.mask);
    for (const op of this.history.slice(from)) {
      if (op.kind === 'stroke') op.points.forEach((_, i) => this.drawSegment(op, i));
    }
    this.redraw();
    this.onChange();
  }

  private drawSegment(s: Extract<Op, { kind: 'stroke' }>, i: number) {
    const ctx = this.ctx;
    ctx.globalCompositeOperation = s.tool === 'paint' ? 'source-over' : 'destination-out';
    ctx.strokeStyle = ctx.fillStyle = '#fff';
    ctx.lineWidth = s.width;
    ctx.lineCap = ctx.lineJoin = 'round';
    const [x, y] = s.points[i];
    if (i === 0) {
      ctx.beginPath();
      ctx.arc(x, y, s.width / 2, 0, Math.PI * 2);
      ctx.fill();
    } else {
      const [px, py] = s.points[i - 1];
      ctx.beginPath();
      ctx.moveTo(px, py);
      ctx.lineTo(x, y);
      ctx.stroke();
    }
    ctx.globalCompositeOperation = 'source-over';
  }

  private scheduleRedraw() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      this.redraw();
    });
  }

  private redraw() {
    const ctx = this.display.getContext('2d')!;
    ctx.globalCompositeOperation = 'source-over';
    ctx.clearRect(0, 0, this.width, this.height);
    ctx.drawImage(this.mask, 0, 0);
    ctx.globalCompositeOperation = 'source-in';
    ctx.fillStyle = this.tint;
    ctx.fillRect(0, 0, this.width, this.height);
    ctx.globalCompositeOperation = 'source-over';
  }

  dispose() {
    cancelAnimationFrame(this.frame);
  }
}
